<script setup lang="ts">
const userStore = useUserStore()
const { t } = useI18n()
const { offerCards } = useContentCardStore()
const route = useRoute()

const isSubPage = computed(() => /^\/my-account\/.+/.test(route.path))

const links = computed(() => [
  {
    text: t('my_account.side_menu.links.contact_details'),
    url: '/my-account/contact-details',
    linkAuthenticated: true,
    icon: 'user',
    showChevron: true,
  },
  {
    text: t('my_account.side_menu.links.order_history'),
    url: '/my-account/order-history',
    linkAuthenticated: true,
    icon: 'bag',
    showChevron: true,
  },
  {
    text: t('my_account.side_menu.links.wishlist'),
    url: '/my-account/wishlist',
    linkAuthenticated: true,
    icon: 'heart-border',
    showChevron: true,
  },
  {
    text: t('my_account.side_menu.links.my_offers'),
    url: '/my-account/my-offers',
    linkAuthenticated: false,
    icon: 'offer',
    notification: true,
    showChevron: true,
  },
  {
    text: t('my_account.side_menu.links.privacy_policy'),
    url: '/privacy-policy',
    linkAuthenticated: false,
    icon: 'privacy',
    showChevron: false,
  },
])

const filteredLinks = computed(() =>
  links.value.filter((link) => !link.linkAuthenticated || link.linkAuthenticated === userStore.isLoggedIn),
)

const handleLogout = async () => {
  try {
    await userStore.logout()
  } catch (e) {
    console.error('handleLogout/error', e)
  }
}
</script>

<template>
  <nav class="side-menu" :class="{ 'side-menu--sub-page': isSubPage }">
    <Anchor v-if="isSubPage" url="/my-account" class="side-menu__back-link">
      <svg-icon name="mhj:chevron-right" class="side-menu__back-icon" />
      {{ $t('my_account.side_menu.header.my_account') }}
    </Anchor>
    <h1 v-else class="side-menu__title">{{ $t('my_account.side_menu.header.my_account') }}</h1>
    <ul class="side-menu-card__list">
      <li v-if="!userStore.isLoggedIn">
        <Action variant="text" class="side-menu__link" action="mhj-signup-panel">
          <div class="side-menu__card-link">
            <svg-icon name="mhj:user" class="side-menu__icon" />
            <div class="side-menu__content">
              <span class="side-menu__title">{{ $t('my_account.side_menu.links.register') }}</span>
            </div>
            <svg-icon name="mhj:chevron-right" class="side-menu__chevron" />
          </div>
        </Action>
      </li>
      <li
        v-for="{ text, url, icon, notification, showChevron } in filteredLinks"
        :key="text"
        class="side-menu-card__item"
      >
        <Anchor
          :url
          :ga-options="{ group: 'my account', info: { location: 'sidebar' } }"
          :log-click="true"
          class="side-menu__link"
        >
          <div class="side-menu__card-link">
            <svg-icon :name="`mhj:${icon}`" class="side-menu__icon" />
            <div class="side-menu__content">
              <span class="side-menu__title">{{ text }}</span>
              <ContentNotifcation v-if="notification" />
            </div>
            <p v-if="notification && offerCards?.length" class="side-menu__notification-count">
              ({{ offerCards?.length }})
            </p>
            <svg-icon v-if="showChevron" name="mhj:chevron-right" class="side-menu__chevron" />
          </div>
        </Anchor>
      </li>
      <li v-if="userStore.isLoggedIn">
        <button class="side-menu__link side-menu__logout" data-testid="side-menu__logout" @click="handleLogout">
          <div class="side-menu__card-link">
            <svg-icon name="mhj:arrow-back-nonchevron" class="side-menu__icon" />
            <div class="side-menu__content">
              <span class="side-menu__title">{{ $t('my_account.side_menu.links.sign_out') }}</span>
            </div>
          </div>
        </button>
      </li>
    </ul>
  </nav>
</template>

<style lang="postcss">
.side-menu {
  > .side-menu__title {
    white-space: nowrap;
    padding-top: var(--space-70);
    padding-bottom: var(--space-70);
    align-self: flex-start;
  }

  .side-menu__back-link {
    display: flex;
    align-items: center;
    align-self: flex-start;
    gap: var(--space-20);
    text-decoration: none;
    font-size: var(--text-small);
    font-weight: 400;
    line-height: 120%;
    letter-spacing: 0.24px;

    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: var(--space-60);

    @media (--tablet) {
      margin-bottom: 0;
      padding-top: var(--space-70);
      padding-bottom: var(--space-70);
    }

    .side-menu__back-icon {
      display: flex;
      width: var(--space-50);
      height: var(--space-50);
      justify-content: center;
      align-items: center;
      transform: rotate(180deg);
    }
  }

  .side-menu-card__list {
    display: flex;
    flex-direction: column;
    row-gap: var(--space-50);
    width: 100%;
    order: 1;

    @media (--tablet) {
      width: 18.5rem;
      order: unset;
    }
  }

  &.side-menu--sub-page .side-menu-card__list {
    display: none;

    @media (--tablet) {
      display: flex;
    }
  }

  .side-menu__link {
    text-decoration: none;
    display: block;
    width: 100%;
  }

  button.side-menu__logout {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    text-align: left;
  }

  .side-menu-card__list .action-wrapper {
    display: block;
    width: 100%;

    .action {
      display: block;
      width: 100%;
      text-align: left;
    }
  }

  .side-menu__card-link {
    display: flex;
    height: var(--space-90);
    padding: var(--space-50);
    gap: var(--space-30);
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    border: 1px solid var(--border-neutral-30);
    font-weight: bold;
    letter-spacing: var(--letter-spacing-1);
    text-decoration: none;
    text-transform: uppercase;
    transition: var(--transition-1);
    width: 100%;

    &:hover {
      border-color: var(--border-dark-10);
    }

    .side-menu__icon {
      display: flex;
      align-items: center;
      width: 1.25rem;
      height: 1.25rem;
      flex-shrink: 0;
    }

    .side-menu__content {
      flex: 1;
      min-width: 0;
      position: relative;

      .side-menu__title {
        margin-block: unset;
      }

      .content-card-notification {
        position: absolute;
        right: -0.4rem;
        top: -0.125rem;
      }
    }

    .side-menu__notification-count {
      margin-left: var(--space-20);
      margin-bottom: 0;
      font-size: var(--text-small);
      flex-shrink: 0;
      @media (--desktop) {
        font-size: var(--text-base);
      }
    }

    .side-menu__chevron {
      width: var(--space-60);
      height: var(--space-60);
      flex-shrink: 0;
      margin-left: auto;
      transition: var(--transition-1);
    }

    &:hover .side-menu__chevron {
      transform: translateX(var(--space-20));
    }
  }
}
</style>
